import { Params } from "@angular/router";

import * as _ from "lodash";

import { ParserHelper } from "./parser-helper.service";

export type RoutesDefinition<T> = KeysAs<T, RouteDefinition>;

export interface RouteDefinition<P = unknown> {
    path: (...args: unknown[]) => unknown[];
    params?: (values?: P) => P;
}

export namespace RouteHelper {
    export type MappingRules<T> = { [K in keyof T]: MappingRule<T[K]> };
    export type MappingRule<T = any> = ((value: any) => T) | ParserHelper.Primitives | Array<any>;
    export type MappingIntersectionWith<T2> = { [K in keyof T2]: any };

    export interface MappingOptions {
        noInherit?: boolean; // don't inherit values from query params that are not covered with mapping rules
    }

    export function map<ToType extends MappingIntersectionWith<FromType>, FromType extends Params = Params>(
        params: Partial<FromType>,
        rules: Partial<MappingRules<ToType>>,
        options: MappingOptions = {}
    ): ToType {
        const result: ToType = options.noInherit ? ({} as ToType) : ({ ...params } as ToType);
        Object.keys(rules).forEach((key: keyof FromType) => {
            const rule = rules[key];
            const value = params[key];
            if (rule && value !== null && value !== undefined) {
                result[key] = parse(rule, value);
            }
        });
        return result;
    }

    export function parse(rule: MappingRule, value: any): any {
        switch (rule) {
            case Boolean:
                return ParserHelper.toBoolean(value, true);
            case Number:
                return ParserHelper.toInt(value);
            case Date:
                return ParserHelper.toDate(value);
            case String:
                return ParserHelper.toString(value);
            case Object:
                return ParserHelper.toObject(value);
            default:
                if (_.isFunction(rule)) {
                    return rule(value);
                } else if (rule instanceof Array) {
                    const values = String(value || "").split(",");
                    return values.map((item, index) => {
                        const type = rule[index] || rule[0];
                        return RouteHelper.parse(type, item);
                    });
                } else {
                    return value;
                }
        }
    }
}
